import Phaser from "phaser";
import { Callbacks, type Room } from "@colyseus/sdk";
import {
  APPEARANCES,
  BASIC_ATTACK_EFFECT_IDS,
  missileAssetPath,
  missileTextureKey,
  scaleForTile,
  sourceOutfitFrame,
  type SpriteAppearance,
  visualMissileByResourceId,
  VISUAL_EFFECTS,
  VISUAL_MISSILES,
} from "@ninja/asset-catalog";
import {
  ENTITY_DIRECTION,
  PROJECTILE_DIRECTIONS,
  REGION_ENCOUNTER_TIMERS_MESSAGE,
  ROOM_MESSAGES,
  regionEncounterTimersEventSchema,
  projectileDirectionFromVector,
  type ProjectileDirection,
  type EntityState,
  type InstanceState,
  type RegionEncounterTimersSnapshot,
} from "@ninja/contracts";
import {
  type MapDefinition,
  type TechniqueCastVisualDefinition,
} from "@ninja/game-content";
import { FreeCameraController } from "../camera/FreeCameraController.js";
import {
  TouchGestureTracker,
  type TouchGestureAction,
} from "../camera/TouchGestureTracker.js";
import {
  BufferedRoomEvents,
  type BufferedRoomEvent,
} from "../network/BufferedRoomEvents.js";
import {
  EntityAnimationController,
  SERVER_TO_SPRITE_DIRECTION,
  outfitTextureKey,
} from "../rendering/EntityAnimationController.js";
import {
  LootFeedbackController,
  type CombatLootPayload,
} from "../ui/LootFeedback.js";
import {
  isTileInsideMap,
  screenPointToTile,
  worldPointToTile,
} from "../tile-coordinates.js";
import { TerrainChunkRenderer } from "../rendering/TerrainChunkRenderer.js";
import { BossEntranceMarkerRenderer } from "../rendering/BossEntranceMarkerRenderer.js";
import { MapTransitionMarkerRenderer } from "../rendering/MapTransitionMarkerRenderer.js";
import { RegionEncounterTimerMapRenderer } from "../rendering/RegionEncounterTimerMapRenderer.js";
import { transformTileCanvas } from "../rendering/transformTileCanvas.js";

const GAME_ROOM_MESSAGE_TYPES = [
  "combat.attack",
  "combat.cast",
  "combat.projectile",
  "combat.projectile_armed",
  "combat.telegraph",
  "combat.field_placed",
  "combat.field_pulse",
  "combat.field_removed",
  "combat.trap_placed",
  "combat.trap_armed",
  "combat.trap_removed",
  "combat.impact",
  "combat.hit",
  "combat.miss",
  "combat.heal",
  "combat.status_applied",
  "combat.status_resisted",
  "combat.status_removed",
  "combat.shield_applied",
  "combat.shield_removed",
  "combat.guard_started",
  "combat.guard_triggered",
  "combat.counter_ready",
  "combat.counter_consumed",
  "combat.reactive_expired",
  "combat.death",
  "combat.loot",
  "combat.reward",
  "region.progress",
  "region.boss_victory",
  "boss.phase",
  "boss.hazard_telegraph",
  "boss.hazard_active",
  "boss.hazard_removed",
  "boss.adds_spawned",
  "boss.wipe_reset",
  "ninja.incapacitated",
  "combat.xp",
  "ninja.level_up",
  "monster.spawned",
  "monster.respawned",
  "match.started",
  "match.finished",
  "command.rejected",
] as const;
const VISUAL_EFFECT_FRAME_DURATION_MS = 55;

type InstanceStateLike = InstanceState;
type EntityStateLike = EntityState;

function effectFacing(direction?: number): {
  x: number;
  y: number;
  rotation: number;
} {
  switch (direction) {
    case ENTITY_DIRECTION.east:
      return { x: 1, y: 0, rotation: 0 };
    case ENTITY_DIRECTION.south:
      return { x: 0, y: 1, rotation: Math.PI / 2 };
    case ENTITY_DIRECTION.west:
      return { x: -1, y: 0, rotation: Math.PI };
    case ENTITY_DIRECTION.north:
    default:
      return { x: 0, y: -1, rotation: -Math.PI / 2 };
  }
}

interface GameRoomEventPayload {
  sourceId?: string;
  targetId?: string;
  entityId?: string;
  techniqueId?: string;
  techniqueLevel?: number;
  action?: string;
  effectId?: number | null;
  missileId?: number;
  projectileDirection?: ProjectileDirection;
  flightDurationMs?: number;
  statusEffectId?: number;
  debuffDurationMs?: number;
  direction?: number;
  castVisual?: TechniqueCastVisualDefinition;
  status?: string;
  x?: number;
  y?: number;
  toX?: number;
  toY?: number;
  fromX?: number;
  fromY?: number;
  damage?: number;
  mitigated?: number;
  healing?: number;
  absorbed?: number;
  shieldId?: string;
  shieldAmount?: number;
  expiresAt?: number;
  critical?: boolean;
  durationMs?: number;
  radius?: number;
  linePoints?: Array<{ x: number; y: number }>;
  lineWidth?: number;
  trapId?: string;
  fieldId?: string;
  pulseIndex?: number;
  magnitude?: number;
  remainingArmMs?: number;
  remainingLifetimeMs?: number;
  armed?: boolean;
  reason?: string;
  sequence?: number;
  attackAt?: number;
  xp?: number;
  experienceLost?: number;
  levelsGained?: number;
  outcome?: string;
  winnerUsername?: string;
  code?: string;
  items?: Array<{
    dropId: string;
    itemId: string;
    name: string;
    quantity: number;
    rarity: string;
    tier: number;
  }>;
  monsterId?: string;
  eventId?: string;
  hazardId?: string;
  sectorId?: string;
  bossName?: string;
  monsterName?: string;
  count?: number;
  preservedSealLabel?: string;
  width?: number;
  height?: number;
  startsAt?: number;
  phase?: number;
  enraged?: boolean;
}

interface TechniqueEventDiagnostic {
  eventId?: string;
  sequence?: number;
  roomId: string;
  mode: "pve" | "pvp";
  type: string;
  techniqueId: string;
  effectId?: number | null;
}

interface TechniqueVisualDiagnostic extends TechniqueEventDiagnostic {
  kind: "effect" | "missile" | "trap_marker" | "field_marker" | "status_loop";
  resourceId?: number;
  textureKey?: string;
  textureAvailable?: boolean;
}

export interface GameSceneCallbacks {
  onFocusedNinjaChanged: (entity: EntityStateLike | null) => void;
  onShutdown: () => void;
  onAssetProgress: (progress: number) => void;
  onAssetError: (error: Error) => void;
  onFirstFrame: () => void;
}
export class GameScene extends Phaser.Scene {
  private room: Room<InstanceStateLike>;
  private localUserId: string;
  private readonly mode: "pve" | "pvp";
  private readonly map: MapDefinition;
  private appearanceLoads = new Set<string>();
  private entityCallbacksBound = false;
  private readonly entityCallbackUnsubscribers: Array<() => void> = [];
  private readonly entityInstanceUnsubscribers = new Map<string, () => void>();
  private readonly roomMessageUnsubscribers: Array<() => void> = [];
  private lootFeedback: LootFeedbackController | undefined;
  private readonly bufferedRoomEvents =
    new BufferedRoomEvents<GameRoomEventPayload>((message) =>
      this.handleRoomEvent(message),
    );
  private animationController!: EntityAnimationController;
  private cameraController!: FreeCameraController;
  private readonly touchGestures = new TouchGestureTracker();
  private initialCameraCentered = false;
  private pendingStateListener:
    ((state: InstanceStateLike) => void) | undefined;
  private rendered = new Map<
    string,
    {
      sprite: Phaser.GameObjects.Sprite;
      hp: Phaser.GameObjects.Graphics;
      label: Phaser.GameObjects.Text;
      selection: Phaser.GameObjects.Graphics;
      ownerId: string;
      animation: string;
      direction: number;
      targetX: number;
      targetY: number;
    }
  >();
  private effects = new Map<string, Phaser.GameObjects.Image>();
  private statusEffectLoops = new Map<
    string,
    {
      targetId: string;
      resourceId: number;
      frames: number[];
      image: Phaser.GameObjects.Image;
      startedAt: number;
      durationMs: number;
      frameIndex: number;
    }
  >();
  private shieldVisuals = new Map<
    string,
    {
      targetId: string;
      resourceId: number;
      frames: number[];
      image: Phaser.GameObjects.Image | undefined;
      halo: Phaser.GameObjects.Graphics;
      startedAt: number;
      frameIndex: number;
    }
  >();
  private proximityTrapVisuals = new Map<
    string,
    {
      resourceId: number;
      frames: number[];
      image: Phaser.GameObjects.Image;
      startedAt: number;
      armedAt: number;
      expiresAt: number;
      frameIndex: number;
      armed: boolean;
    }
  >();
  private techniqueFieldVisuals = new Map<
    string,
    Phaser.GameObjects.Graphics
  >();
  private bossHazardVisuals = new Map<
    string,
    { marker: Phaser.GameObjects.Graphics; active: boolean }
  >();
  private readonly diagnosticTechniqueEvents: TechniqueEventDiagnostic[] = [];
  private readonly diagnosticTechniqueVisuals: TechniqueVisualDiagnostic[] = [];
  private activeTechniqueDiagnostic: TechniqueEventDiagnostic | undefined;
  private terrainAnimations: Array<{
    sprite: Phaser.GameObjects.Image;
    frames: string[];
    durations: number[];
    totalDuration: number;
    currentFrame: number;
    startTime: number;
    tile: MapDefinition["animatedTiles"][number];
  }> = [];
  private flippedTileCanvases = new Map<string, HTMLCanvasElement>();
  private terrainRenderer: TerrainChunkRenderer | undefined;
  private bossEntranceMarkers: BossEntranceMarkerRenderer | undefined;
  private mapTransitionMarkers: MapTransitionMarkerRenderer | undefined;
  private encounterTimerMarkers: RegionEncounterTimerMapRenderer | undefined;
  private encounterTimerSnapshot: RegionEncounterTimersSnapshot | undefined;
  private selection = new Set<string>();
  private focusedNinjaId: string | undefined;
  private readonly callbacks: GameSceneCallbacks;
  private lastMoveSent = 0;
  private moveCommandCount = 0;
  private lastMoveTile = "";
  private lastCommandRejection = "";
  private lastDiagnosticsAt = 0;

  constructor(
    room: Room<InstanceStateLike>,
    localUserId: string,
    mode: "pve" | "pvp",
    callbacks: GameSceneCallbacks,
    map: MapDefinition,
  ) {
    super({ key: "shared-game-scene" });
    this.room = room;
    this.localUserId = localUserId;
    this.mode = mode;
    this.map = map;
    this.callbacks = callbacks;
    this.animationController = new EntityAnimationController(this);
    for (const type of GAME_ROOM_MESSAGE_TYPES)
      this.listenRoomMessage(type, (payload: GameRoomEventPayload) =>
        this.bufferedRoomEvents.push({ type, payload }),
      );
    this.listenRoomMessage(
      REGION_ENCOUNTER_TIMERS_MESSAGE,
      (event: unknown) => {
        const snapshot = regionEncounterTimersEventSchema.safeParse(event);
        if (!snapshot.success) return;
        this.encounterTimerSnapshot = snapshot.data;
        this.encounterTimerMarkers?.update(snapshot.data);
      },
    );
  }

  preload(): void {
    this.load.on("progress", this.callbacks.onAssetProgress);
    this.load.once("loaderror", (file: { key?: string; src?: string }) => {
      const asset = file.src ?? file.key ?? "unknown asset";
      this.callbacks.onAssetError(
        new Error(`Could not load a required game asset: ${asset}`),
      );
    });
    for (const terrain of this.map.terrainAssets)
      this.load.image(`ground-${terrain.assetKey}`, `/${terrain.path}`);
    for (const decoration of this.map.decorations)
      this.load.image(
        `map-object-${decoration.assetId}`,
        `/${decoration.path}`,
      );
    const appearanceIds = new Set(
      Array.from(this.room.state?.entities?.values?.() ?? []).map(
        (entity) => entity.appearanceId,
      ),
    );
    for (const appearanceId of appearanceIds) {
      const appearance = APPEARANCES[appearanceId];
      if (appearance) this.queueAppearanceAssets(appearance);
    }
    for (const effect of Object.values(VISUAL_EFFECTS)) {
      for (const frame of effect.frames)
        this.load.image(
          `effect-${effect.resourceId}-f${frame}`,
          `/assets/effects/effect_${effect.resourceId}_f${frame}.png`,
        );
    }
    for (const missile of Object.values(VISUAL_MISSILES)) {
      for (const direction of PROJECTILE_DIRECTIONS) {
        const variant = missile.directions[direction];
        const frames = variant.frames ?? [undefined];
        for (const frame of frames)
          this.load.image(
            missileTextureKey(missile.resourceId, direction, frame),
            `/${missileAssetPath(missile, direction, frame)}`,
          );
      }
    }
  }

  private tileCanvasSource(
    assetKey: string,
    flipHorizontal: boolean,
    flipVertical: boolean,
    flipDiagonal: boolean,
  ): HTMLImageElement | HTMLCanvasElement {
    const source = this.textures.get(`ground-${assetKey}`).getSourceImage() as
      HTMLImageElement | HTMLCanvasElement;
    if (!flipHorizontal && !flipVertical && !flipDiagonal) return source;
    const key = `${assetKey}:${Number(flipHorizontal)}${Number(flipVertical)}${Number(flipDiagonal)}`;
    const cached = this.flippedTileCanvases.get(key);
    if (cached) return cached;
    const asset = this.terrainAsset(assetKey);
    const canvas = transformTileCanvas(
      source,
      asset.width,
      asset.height,
      flipHorizontal,
      flipVertical,
      flipDiagonal,
    );
    this.flippedTileCanvases.set(key, canvas);
    return canvas;
  }

  private textureKeyForMapTile(
    assetKey: string,
    tile: MapDefinition["animatedTiles"][number],
  ): string {
    if (!tile.initialFlipX && !tile.initialFlipY && !tile.initialFlipDiagonal)
      return `ground-${assetKey}`;
    const key = `ground-flip-${assetKey}-${Number(tile.initialFlipX)}${Number(tile.initialFlipY)}${Number(tile.initialFlipDiagonal)}`;
    if (this.textures.exists(key)) return key;
    const texture = this.textures.createCanvas(
      key,
      this.tileCanvasSource(
        assetKey,
        tile.initialFlipX,
        tile.initialFlipY,
        tile.initialFlipDiagonal,
      ).width,
      this.tileCanvasSource(
        assetKey,
        tile.initialFlipX,
        tile.initialFlipY,
        tile.initialFlipDiagonal,
      ).height,
    );
    if (!texture)
      throw new Error(`Could not allocate animated tile texture ${key}`);
    texture
      .getContext()
      .drawImage(
        this.tileCanvasSource(
          assetKey,
          tile.initialFlipX,
          tile.initialFlipY,
          tile.initialFlipDiagonal,
        ),
        0,
        0,
      );
    texture.refresh();
    return key;
  }

  private terrainAsset(
    assetKey: string,
  ): MapDefinition["terrainAssets"][number] {
    const asset = this.map.terrainAssets.find(
      (entry) => entry.assetKey === assetKey,
    );
    if (!asset) throw new Error(`Terrain asset ${assetKey} is missing`);
    return asset;
  }

  create(): void {
    const map = this.map;
    const lootFeedbackRoot = document.querySelector<HTMLElement>(
      "#loot-feedback-root",
    );
    const lootHistoryToggleHost = document.querySelector<HTMLElement>(
      "#loot-history-toggle-mount",
    );
    if (lootFeedbackRoot)
      this.lootFeedback = new LootFeedbackController(
        lootFeedbackRoot,
        lootHistoryToggleHost ?? undefined,
      );
    const bounds = map.cameraBounds;
    for (const tile of map.animatedTiles) {
      const layerDepth =
        map.tileLayers.find((layer) => layer.name === tile.layer)?.depth ?? -18;
      const asset = this.terrainAsset(tile.frameAssetKeys[0]!);
      const width = tile.initialFlipDiagonal ? asset.height : asset.width;
      const height = tile.initialFlipDiagonal ? asset.width : asset.height;
      const sprite = this.add
        .image(
          tile.x + asset.tileOffsetX + (width - map.tileSize) / 2,
          tile.y + asset.tileOffsetY + (map.tileSize - height) / 2,
          this.textureKeyForMapTile(tile.frameAssetKeys[0]!, tile),
        )
        .setDisplaySize(width, height)
        .setDepth(layerDepth + 0.1);
      this.terrainAnimations.push({
        sprite,
        frames: tile.frameAssetKeys,
        durations: tile.frameDurationsMs,
        totalDuration: tile.frameDurationsMs.reduce(
          (sum, value) => sum + value,
          0,
        ),
        currentFrame: 0,
        startTime: 0,
        tile,
      });
    }
    for (const decoration of map.decorations) {
      this.add
        .image(
          decoration.x + decoration.width / 2,
          decoration.y + decoration.height / 2,
          `map-object-${decoration.assetId}`,
        )
        .setDepth(decoration.depth);
    }
    for (const obstacle of map.obstacles) {
      if (obstacle.renderColor !== undefined)
        this.add
          .rectangle(
            obstacle.x + obstacle.width / 2,
            obstacle.y + obstacle.height / 2,
            obstacle.width,
            obstacle.height,
            obstacle.renderColor,
            obstacle.renderAlpha ?? 0.92,
          )
          .setStrokeStyle(4, 0xb18a5b, 0.9)
          .setDepth(bounds.y + obstacle.y + obstacle.height);
    }
    this.bossEntranceMarkers = new BossEntranceMarkerRenderer(this, map);
    this.mapTransitionMarkers = new MapTransitionMarkerRenderer(this, map);
    this.encounterTimerMarkers = new RegionEncounterTimerMapRenderer(this, map);
    if (this.encounterTimerSnapshot)
      this.encounterTimerMarkers.update(this.encounterTimerSnapshot);
    this.add
      .rectangle(
        bounds.x + bounds.width / 2,
        bounds.y + bounds.height / 2,
        bounds.width - 6,
        bounds.height - 6,
      )
      .setStrokeStyle(4, 0xb8c99b, 0.38)
      .setDepth(30);
    const ownedNinjas = [
      ...(this.room.state.entities?.values?.() ?? []),
    ].filter(
      (entity) =>
        entity.entityType === "ninja" && entity.ownerId === this.localUserId,
    );
    const initialCenter = ownedNinjas.length
      ? ownedNinjas.reduce(
          (point, entity) => ({ x: point.x + entity.x, y: point.y + entity.y }),
          { x: 0, y: 0 },
        )
      : {
          x: bounds.x + bounds.width / 2,
          y: bounds.y + bounds.height / 2,
        };
    this.cameraController = new FreeCameraController(this, {
      bounds,
      initialCenter: {
        x: initialCenter.x / (ownedNinjas.length || 1),
        y: initialCenter.y / (ownedNinjas.length || 1),
      },
    });
    this.terrainRenderer = new TerrainChunkRenderer(this, map);
    this.terrainRenderer.sync();
    this.initialCameraCentered = ownedNinjas.length > 0;
    this.input.on("pointerdown", this.handlePointerDown, this);
    this.input.on("pointermove", this.handlePointerMove, this);
    this.input.on("pointerup", this.handlePointerUp, this);
    this.input.on("pointerupoutside", this.handlePointerUpOutside, this);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.selection.clear();
      this.focusedNinjaId = undefined;
      this.callbacks.onFocusedNinjaChanged(null);
      this.callbacks.onShutdown();
      this.lootFeedback?.dispose();
      this.lootFeedback = undefined;
      this.input.off("pointerdown", this.handlePointerDown, this);
      this.input.off("pointermove", this.handlePointerMove, this);
      this.input.off("pointerup", this.handlePointerUp, this);
      this.input.off("pointerupoutside", this.handlePointerUpOutside, this);
      this.touchGestures.reset();
      this.cameraController?.dispose();
      this.terrainRenderer?.dispose();
      this.terrainRenderer = undefined;
      this.bossEntranceMarkers?.destroy();
      this.bossEntranceMarkers = undefined;
      this.mapTransitionMarkers?.destroy();
      this.mapTransitionMarkers = undefined;
      this.encounterTimerMarkers?.destroy();
      this.encounterTimerMarkers = undefined;
      this.encounterTimerSnapshot = undefined;
      for (const unsubscribe of this.entityCallbackUnsubscribers.splice(0))
        unsubscribe();
      for (const unsubscribe of this.entityInstanceUnsubscribers.values())
        unsubscribe();
      this.entityInstanceUnsubscribers.clear();
      for (const unsubscribe of this.roomMessageUnsubscribers.splice(0))
        unsubscribe();
      for (const loop of this.statusEffectLoops.values()) loop.image.destroy();
      this.statusEffectLoops.clear();
      for (const shield of this.shieldVisuals.values()) {
        shield.image?.destroy();
        shield.halo.destroy();
      }
      this.shieldVisuals.clear();
      for (const trap of this.proximityTrapVisuals.values())
        trap.image.destroy();
      this.proximityTrapVisuals.clear();
      for (const field of this.techniqueFieldVisuals.values()) field.destroy();
      this.techniqueFieldVisuals.clear();
      for (const hazard of this.bossHazardVisuals.values())
        hazard.marker.destroy();
      this.bossHazardVisuals.clear();
      this.bufferedRoomEvents.reset();
    });
    if (!this.bindEntityCallbacks()) {
      this.pendingStateListener = (state) => {
        if (state.entities) this.bindEntityCallbacks();
      };
      this.room.onStateChange(this.pendingStateListener);
      this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
        if (this.pendingStateListener)
          this.room.onStateChange.remove(this.pendingStateListener);
        this.pendingStateListener = undefined;
      });
    }
    this.bufferedRoomEvents.flush();
    this.room.send("combat.traps.sync", {});
    this.room.send("boss.encounter.sync", {});
    this.events.once(Phaser.Scenes.Events.RENDER, () =>
      this.callbacks.onFirstFrame(),
    );
  }

  private handleRoomEvent(
    message: BufferedRoomEvent<GameRoomEventPayload>,
  ): void {
    const { type, payload } = message;
    const diagnostic = this.recordTechniqueEvent(type, payload);
    if (type === "command.rejected") {
      this.lastCommandRejection = payload.code ?? "UNKNOWN";
      return;
    }
    if (type === "match.finished") {
      const messageElement =
        document.querySelector<HTMLElement>("#game-message");
      if (messageElement) {
        messageElement.textContent =
          payload.outcome === "draw"
            ? "MATCH DRAW"
            : `${payload.winnerUsername ?? "A player"} WINS`;
        messageElement.classList.add("visible");
      }
      return;
    }
    if (type === "ninja.incapacitated") {
      const messageElement =
        document.querySelector<HTMLElement>("#game-message");
      if (messageElement) {
        messageElement.textContent = `NINJA INCAPACITATED · -${payload.experienceLost ?? 0} XP · HOSPITAL`;
        messageElement.classList.add("visible");
      }
      return;
    }
    if (type === "combat.loot") {
      if (this.room.state.mode === "pve")
        this.lootFeedback?.present(payload as CombatLootPayload);
      return;
    }
    if (type === "boss.hazard_telegraph") {
      this.showBossHazard(payload, false);
      return;
    }
    if (type === "boss.hazard_active") {
      this.showBossHazard(payload, true);
      return;
    }
    if (type === "boss.hazard_removed") {
      this.removeBossHazard(payload.hazardId);
      return;
    }
    if (type === "boss.phase") {
      if (payload.entityId)
        this.showReactiveLabel(
          payload.entityId,
          payload.enraged ? "ENRAGED" : `PHASE ${payload.phase ?? 1}`,
          payload.enraged ? "#ff684c" : "#ffd269",
        );
      return;
    }
    if (type === "boss.adds_spawned") {
      this.showEncounterToast(
        `${payload.bossName ?? "The boss"} summons ${payload.count ?? 0} ${payload.monsterName ?? "reinforcements"}.`,
      );
      return;
    }
    if (type === "boss.wipe_reset") {
      for (const hazardId of this.bossHazardVisuals.keys())
        this.removeBossHazard(hazardId);
      this.showEncounterToast(
        `The party was wiped. ${payload.bossName ?? "The boss"} resets; your ${payload.preservedSealLabel ?? "regional seal"} is preserved.`,
      );
      return;
    }
    if (type === "combat.xp" || type === "ninja.level_up") {
      const entityId = payload.entityId;
      const view = entityId ? this.rendered.get(entityId) : undefined;
      if (!view) return;
      const label = this.add
        .text(
          view.sprite.x,
          view.sprite.y - 36,
          type === "ninja.level_up"
            ? `LEVEL UP${payload.levelsGained ? ` +${payload.levelsGained}` : ""}`
            : `+${payload.xp ?? 0} XP`,
          {
            color: type === "ninja.level_up" ? "#ffd269" : "#e8e2bb",
            fontFamily: "monospace",
            fontSize: "12px",
            stroke: "#182018",
            strokeThickness: 3,
          },
        )
        .setOrigin(0.5)
        .setDepth(1000);
      this.tweens.add({
        targets: label,
        y: label.y - 34,
        alpha: 0,
        duration: 900,
        onComplete: () => label.destroy(),
      });
      return;
    }
    const previousDiagnostic = this.activeTechniqueDiagnostic;
    this.activeTechniqueDiagnostic = diagnostic;
    try {
      this.playEvent(type, payload);
    } finally {
      this.activeTechniqueDiagnostic = previousDiagnostic;
    }
  }

  private recordTechniqueEvent(
    type: string,
    payload: GameRoomEventPayload,
  ): TechniqueEventDiagnostic | undefined {
    if (!payload.techniqueId || !document.querySelector("#game-diagnostics"))
      return undefined;
    const diagnostic: TechniqueEventDiagnostic = {
      ...(payload.eventId ? { eventId: payload.eventId } : {}),
      ...(payload.sequence === undefined ? {} : { sequence: payload.sequence }),
      roomId: this.room.state.instanceId,
      mode: this.mode,
      type,
      techniqueId: payload.techniqueId,
      ...(payload.effectId === undefined ? {} : { effectId: payload.effectId }),
    };
    this.diagnosticTechniqueEvents.push(diagnostic);
    if (this.diagnosticTechniqueEvents.length > 60)
      this.diagnosticTechniqueEvents.shift();
    return diagnostic;
  }

  private recordTechniqueVisual(
    kind: TechniqueVisualDiagnostic["kind"],
    resourceId?: number,
    source = this.activeTechniqueDiagnostic,
    textureKey?: string,
  ): void {
    if (!source) return;
    this.diagnosticTechniqueVisuals.push({
      ...source,
      kind,
      ...(resourceId === undefined ? {} : { resourceId }),
      ...(textureKey === undefined
        ? {}
        : { textureKey, textureAvailable: this.textures.exists(textureKey) }),
    });
    if (this.diagnosticTechniqueVisuals.length > 60)
      this.diagnosticTechniqueVisuals.shift();
  }

  private listenRoomMessage<T>(
    type: string,
    callback: (event: T) => void,
  ): void {
    this.roomMessageUnsubscribers.push(
      this.room.onMessage(type, callback as (event: unknown) => void),
    );
  }

  update(time: number, delta: number): void {
    this.cameraController?.update(delta, this.selectedSquadCentroid());
    this.terrainRenderer?.sync();
    this.updateStatusEffectLoops(performance.now());
    this.updateProximityTrapVisuals(time);
    this.syncTechniqueFieldVisuals(time);
    for (const animation of this.terrainAnimations) {
      if (animation.frames.length < 2 || animation.totalDuration <= 0) continue;
      if (animation.startTime === 0) animation.startTime = time;
      let elapsed = (time - animation.startTime) % animation.totalDuration;
      let frameIndex = 0;
      while (elapsed >= animation.durations[frameIndex]!) {
        elapsed -= animation.durations[frameIndex]!;
        frameIndex = (frameIndex + 1) % animation.frames.length;
      }
      if (frameIndex !== animation.currentFrame) {
        animation.currentFrame = frameIndex;
        animation.sprite.setTexture(
          this.textureKeyForMapTile(
            animation.frames[frameIndex]!,
            animation.tile,
          ),
        );
      }
    }
    const blend = 1 - Math.exp(-Math.max(0, delta) / 45);
    for (const view of this.rendered.values()) {
      const sprite = view.sprite;
      sprite.x += (view.targetX - sprite.x) * blend;
      sprite.y += (view.targetY - sprite.y) * blend;
      if (Math.abs(view.targetX - sprite.x) < 0.15) sprite.x = view.targetX;
      if (Math.abs(view.targetY - sprite.y) < 0.15) sprite.y = view.targetY;
      sprite.setDepth(sprite.y);
      view.selection.setPosition(sprite.x, sprite.y).setDepth(sprite.y - 1);
      view.label.setPosition(sprite.x, sprite.y - 42).setDepth(sprite.y + 21);
      view.hp.setPosition(sprite.x - 12, sprite.y - 31).setDepth(sprite.y + 20);
    }
    this.syncShieldVisuals(time);
    for (const { marker, active } of this.bossHazardVisuals.values())
      marker.setAlpha(active ? 0.76 + Math.sin(time / 160) * 0.15 : 0.92);
    this.updateDiagnostics(time);
  }

  private showBossHazard(event: GameRoomEventPayload, active: boolean): void {
    if (
      !event.hazardId ||
      event.x === undefined ||
      event.y === undefined ||
      event.width === undefined ||
      event.height === undefined
    )
      return;
    let visual = this.bossHazardVisuals.get(event.hazardId);
    if (!visual) {
      const marker = this.add
        .graphics()
        .setPosition(event.x, event.y)
        .setDepth(9_000);
      visual = { marker, active };
      this.bossHazardVisuals.set(event.hazardId, visual);
    }
    visual.active = active;
    visual.marker.clear();
    visual.marker.setPosition(event.x, event.y);
    visual.marker.fillStyle(active ? 0xff3c22 : 0xffa62b, active ? 0.24 : 0.1);
    visual.marker.fillRect(0, 0, event.width, event.height);
    visual.marker.lineStyle(active ? 5 : 3, active ? 0xff492e : 0xffd45d, 0.95);
    visual.marker.strokeRect(0, 0, event.width, event.height);
    if (!active) {
      visual.marker.lineStyle(2, 0xffefac, 0.55);
      for (let offset = -event.height; offset < event.width; offset += 32) {
        const x1 = Math.max(0, offset);
        const y1 = x1 - offset;
        const x2 = Math.min(event.width, event.height + offset);
        const y2 = x2 - offset;
        if (x1 <= x2 && y1 <= event.height && y2 >= 0)
          visual.marker.lineBetween(x1, y1, x2, y2);
      }
    }
  }

  private removeBossHazard(hazardId?: string): void {
    if (!hazardId) return;
    this.bossHazardVisuals.get(hazardId)?.marker.destroy();
    this.bossHazardVisuals.delete(hazardId);
  }

  private showEncounterToast(message: string): void {
    const element = document.querySelector<HTMLElement>("#game-message");
    if (!element) return;
    element.textContent = message;
    element.classList.add("visible");
    window.setTimeout(() => element.classList.remove("visible"), 4_000);
  }

  zoomCamera(amount: number): void {
    this.cameraController?.zoomBy(amount);
  }

  centerCamera(): void {
    const center = this.selectedSquadCentroid();
    if (center) this.cameraController?.centerOn(center);
  }

  private handlePointerDown(pointer: Phaser.Input.Pointer): void {
    if (pointer.wasTouch) {
      this.touchGestures.pointerDown(pointer.id, pointer.x, pointer.y);
      return;
    }
    this.handleGameplayTap(pointer);
  }

  private handlePointerUp(pointer: Phaser.Input.Pointer): void {
    if (!pointer.wasTouch) return;
    const action = this.touchGestures.pointerUp(
      pointer.id,
      pointer.x,
      pointer.y,
    );
    if (action?.type === "tap") this.handleGameplayTap(pointer);
  }

  private handlePointerUpOutside(pointer: Phaser.Input.Pointer): void {
    this.touchGestures.pointerCancel(pointer.id);
  }

  private handleTouchGesture(action: TouchGestureAction | undefined): void {
    if (!action) return;
    if (action.type === "pan")
      this.cameraController.panByScreenDelta(action.deltaX, action.deltaY);
    else if (action.type === "zoom")
      this.cameraController.zoomByFactor(action.factor, {
        x: action.x,
        y: action.y,
      });
  }

  private handleGameplayTap(pointer: Phaser.Input.Pointer): void {
    if (pointer.button !== 0) return;
    const tile = screenPointToTile(
      this.cameras.main,
      pointer.x,
      pointer.y,
      this.map.tileSize,
    );
    if (!isTileInsideMap(tile, this.map.width, this.map.height)) return;

    const friendly = [...this.rendered.entries()].find(([id, view]) => {
      const entity = this.room.state.entities.get(id);
      if (
        !view.sprite.visible ||
        view.ownerId !== this.localUserId ||
        entity?.entityType !== "ninja"
      )
        return false;
      const currentTile = worldPointToTile(
        { x: view.sprite.x, y: view.sprite.y },
        this.map.tileSize,
      );
      return currentTile.column === tile.column && currentTile.row === tile.row;
    });
    if (friendly) {
      const id = friendly[0];
      const shift = Boolean(
        (pointer.event as PointerEvent | undefined)?.shiftKey,
      );
      if (shift) {
        if (this.selection.has(id)) this.selection.delete(id);
        else this.selection.add(id);
        if (this.selection.has(id)) this.focusNinja(id);
        else if (this.focusedNinjaId === id)
          this.focusNinja([...this.selection].at(-1));
      } else if (this.selection.size === 1 && this.selection.has(id)) {
        this.clearNinjaSelection();
        return;
      } else {
        this.selection.clear();
        this.selection.add(id);
        this.focusNinja(id);
      }
      this.refreshSelectionIndicators();
      return;
    }

    const worldClick = this.cameras.main.getWorldPoint(pointer.x, pointer.y);
    const clickedMonster = [...this.rendered.entries()].find(([id, view]) => {
      const entity = this.room.state.entities.get(id);
      if (
        !view.sprite.visible ||
        entity?.entityType !== "monster" ||
        !entity.alive
      )
        return false;
      const monsterTile = worldPointToTile(
        { x: view.sprite.x, y: view.sprite.y },
        this.map.tileSize,
      );
      return (
        (monsterTile.column === tile.column && monsterTile.row === tile.row) ||
        view.sprite.getBounds().contains(worldClick.x, worldClick.y)
      );
    });

    const now = performance.now();
    if (now - this.lastMoveSent < 100) return;
    this.lastMoveSent = now;
    const selectedIds = [...this.selection].filter((id) => {
      const view = this.rendered.get(id);
      return view?.ownerId === this.localUserId && view.sprite.visible;
    });
    if (clickedMonster) {
      this.room.send(ROOM_MESSAGES.engage, {
        targetId: clickedMonster[0],
        x: tile.x,
        y: tile.y,
        ...(selectedIds.length > 0 ? { unitIds: selectedIds } : {}),
      });
      this.lastMoveTile = `${tile.column},${tile.row}`;
      return;
    }
    this.room.send(ROOM_MESSAGES.move, {
      x: tile.x,
      y: tile.y,
      ...(selectedIds.length > 0 ? { unitIds: selectedIds } : {}),
    });
    this.moveCommandCount += 1;
    this.lastMoveTile = `${tile.column},${tile.row}`;
  }

  private handlePointerMove(pointer: Phaser.Input.Pointer): void {
    if (pointer.wasTouch)
      this.handleTouchGesture(
        this.touchGestures.pointerMove(pointer.id, pointer.x, pointer.y),
      );
    const tile = screenPointToTile(
      this.cameras.main,
      pointer.x,
      pointer.y,
      this.map.tileSize,
    );
    const hovered = isTileInsideMap(tile, this.map.width, this.map.height)
      ? [...this.rendered.values()].find((view) => {
          if (!view.sprite.visible) return false;
          const entityTile = worldPointToTile(
            { x: view.sprite.x, y: view.sprite.y },
            this.map.tileSize,
          );
          return (
            entityTile.column === tile.column && entityTile.row === tile.row
          );
        })
      : undefined;
    for (const view of this.rendered.values())
      view.label.setVisible(view === hovered);
  }

  private selectedSquadCentroid(): { x: number; y: number } | undefined {
    const selected = [...this.selection]
      .map((id) => this.rendered.get(id))
      .filter((view): view is NonNullable<typeof view> =>
        Boolean(view?.sprite.visible),
      );
    const owned = selected.length
      ? selected
      : [...this.rendered.values()].filter(
          (view) => view.ownerId === this.localUserId && view.sprite.visible,
        );
    if (owned.length === 0) return undefined;
    return owned.reduce(
      (point, view) => ({
        x: point.x + view.targetX / owned.length,
        y: point.y + view.targetY / owned.length,
      }),
      { x: 0, y: 0 },
    );
  }

  private refreshSelectionIndicators(): void {
    for (const [id, view] of this.rendered)
      view.selection.setVisible(this.selection.has(id) && view.sprite.visible);
  }

  clearNinjaSelection(): void {
    this.selection.clear();
    this.focusedNinjaId = undefined;
    this.refreshSelectionIndicators();
    this.callbacks.onFocusedNinjaChanged(null);
  }

  private focusNinja(id: string | undefined): void {
    const entity = id ? this.room.state.entities.get(id) : undefined;
    if (
      !entity ||
      entity.entityType !== "ninja" ||
      entity.ownerId !== this.localUserId
    ) {
      this.focusedNinjaId = undefined;
      this.callbacks.onFocusedNinjaChanged(null);
      return;
    }
    this.focusedNinjaId = id;
    this.callbacks.onFocusedNinjaChanged(entity);
  }

  private updateDiagnostics(time: number): void {
    if (time - this.lastDiagnosticsAt < 100) return;
    this.lastDiagnosticsAt = time;
    const element = document.querySelector<HTMLElement>("#game-diagnostics");
    if (!element) return;
    const camera = this.cameras.main;
    const pointer = this.input.activePointer;
    const mouseWorld = camera.getWorldPoint(pointer.x, pointer.y);
    const tile = screenPointToTile(
      camera,
      pointer.x,
      pointer.y,
      this.map.tileSize,
    );
    const entityLines = [...this.rendered.entries()]
      .filter(([, view]) => view.sprite.visible)
      .slice(0, 8)
      .map(([id, view]) => {
        const cell = worldPointToTile(
          { x: view.targetX, y: view.targetY },
          this.map.tileSize,
        );
        return `${id.slice(0, 8)} ${view.animation}/${view.direction} tile=${cell.column},${cell.row}`;
      });
    const selected = [...this.selection];
    element.dataset.mapId = this.map.id;
    element.dataset.mapWidth = String(this.map.width);
    element.dataset.mapHeight = String(this.map.height);
    element.dataset.cameraWidth = String(camera.width);
    element.dataset.cameraHeight = String(camera.height);
    element.dataset.scrollX = camera.scrollX.toFixed(1);
    element.dataset.scrollY = camera.scrollY.toFixed(1);
    element.dataset.zoom = camera.zoom.toFixed(2);
    element.dataset.pointerScreenX = pointer.x.toFixed(1);
    element.dataset.pointerScreenY = pointer.y.toFixed(1);
    element.dataset.mouseWorldX = mouseWorld.x.toFixed(1);
    element.dataset.mouseWorldY = mouseWorld.y.toFixed(1);
    element.dataset.moveCommandCount = String(this.moveCommandCount);
    element.dataset.selectedIds = selected.join(",");
    element.dataset.hoveredTile = `${tile.column},${tile.row}`;
    element.dataset.lastMoveTile = this.lastMoveTile;
    element.dataset.lastCommandRejection = this.lastCommandRejection;
    element.dataset.roomId = this.room.roomId;
    element.dataset.instanceId = this.room.state.instanceId;
    element.dataset.mode = this.mode;
    element.dataset.phase = this.room.state.phase;
    element.dataset.elapsedMs = String(this.room.state.elapsedMs);
    element.dataset.techniqueEvents = JSON.stringify(
      this.diagnosticTechniqueEvents,
    );
    element.dataset.techniqueVisuals = JSON.stringify(
      this.diagnosticTechniqueVisuals,
    );
    element.dataset.entities = JSON.stringify(
      [...this.rendered.entries()].map(([id, view]) => ({
        id,
        entityType: this.room.state.entities.get(id)?.entityType ?? "unknown",
        ownerId: view.ownerId,
        x: view.targetX,
        y: view.targetY,
        renderX: view.sprite.x,
        renderY: view.sprite.y,
        animation: view.animation,
        direction: view.direction,
        alive: view.sprite.visible,
        hp: this.room.state.entities.get(id)?.hp ?? 0,
        chakra: this.room.state.entities.get(id)?.chakra ?? 0,
      })),
    );
    element.textContent = [
      `camera ${camera.scrollX.toFixed(1)}, ${camera.scrollY.toFixed(1)} · zoom ${camera.zoom.toFixed(2)}`,
      `mouse ${mouseWorld.x.toFixed(1)}, ${mouseWorld.y.toFixed(1)} · tile ${tile.column}, ${tile.row} · selected ${selected.join(", ") || "none"}`,
      `move commands ${this.moveCommandCount} · FPS ${this.game.loop.actualFps.toFixed(1)}`,
      `last rejection ${this.lastCommandRejection || "none"}`,
      ...entityLines,
    ].join("\n");
  }

  private bindEntityCallbacks(): boolean {
    if (this.entityCallbacksBound) return true;
    const entities = this.room.state?.entities;
    if (!entities) return false;

    entities.forEach((entity, key) => this.observeEntity(entity, String(key)));
    const callbacks = Callbacks.get(this.room);
    this.entityCallbackUnsubscribers.push(
      callbacks.onAdd(
        "entities",
        (entity, key) => this.observeEntity(entity, String(key)),
        false,
      ),
      callbacks.onChange("entities", (key, entity) =>
        this.observeEntity(entity, String(key)),
      ),
      callbacks.onRemove("entities", (_entity, key) =>
        this.removeEntity(String(key)),
      ),
    );
    this.entityCallbacksBound = true;
    if (this.pendingStateListener) {
      this.room.onStateChange.remove(this.pendingStateListener);
      this.pendingStateListener = undefined;
    }
    return true;
  }

  private observeEntity(entity: EntityStateLike, entityKey: string): void {
    this.entityInstanceUnsubscribers.get(entityKey)?.();
    this.updateEntity(entity, entityKey);
    this.entityInstanceUnsubscribers.set(
      entityKey,
      Callbacks.get(this.room).onChange(entity, () =>
        this.updateEntity(entity, entityKey),
      ),
    );
  }

  private updateEntity(entity: EntityStateLike, entityKey = entity.id): void {
    let view = this.rendered.get(entityKey);
    const appearance = APPEARANCES[entity.appearanceId];
    if (!appearance || !this.ensureAppearanceLoaded(appearance)) return;
    const direction =
      SERVER_TO_SPRITE_DIRECTION[entity.direction] ??
      appearance.sourceDirections[0] ??
      0;
    const firstFrame = appearance.sourceFrames[0] ?? 0;
    const texture = outfitTextureKey(appearance.id, direction, firstFrame);
    if (!view) {
      const sprite = this.add
        .sprite(entity.x, entity.y, texture, 0)
        .setDepth(entity.y)
        .setOrigin(appearance.anchor.x, appearance.anchor.y)
        .setScale(scaleForTile(appearance, this.map.tileSize));
      const hp = this.add.graphics().setDepth(entity.y + 20);
      const selection = this.add.graphics().setDepth(entity.y - 1);
      selection
        .fillStyle(0x101710, 0.58)
        .fillEllipse(0, 3, 28, 11)
        .lineStyle(1.5, 0xffd45e, 0.96)
        .strokeEllipse(0, 0, 30, 13)
        .setVisible(false);
      const label = this.add
        .text(entity.x, entity.y - 46, entity.name, {
          color: "#fff8df",
          fontFamily: "monospace",
          fontSize: "12px",
          stroke: "#142016",
          strokeThickness: 2,
          backgroundColor: "#142016",
          padding: { x: 3, y: 2 },
        })
        .setOrigin(0.5)
        .setVisible(false)
        .setDepth(entity.y + 21);
      view = {
        sprite,
        hp,
        label,
        selection,
        ownerId: entity.ownerId,
        animation: "idle",
        direction,
        targetX: entity.x,
        targetY: entity.y,
      };
      this.rendered.set(entityKey, view);
    }
    view.ownerId = entity.ownerId;
    view.animation = entity.animation;
    view.direction = direction;
    view.targetX = entity.x;
    view.targetY = entity.y;
    if (
      !this.initialCameraCentered &&
      entity.entityType === "ninja" &&
      entity.ownerId === this.localUserId
    ) {
      this.cameraController.centerOn({ x: entity.x, y: entity.y });
      this.initialCameraCentered = true;
    }
    view.sprite.setVisible(entity.alive);
    view.hp.setVisible(entity.alive);
    this.animationController.apply(
      view.sprite,
      appearance,
      entity.animation,
      direction,
      entity.alive,
    );
    if (!entity.alive) this.selection.delete(entityKey);
    if (!entity.alive) this.stopStatusEffectsForEntity(entityKey);
    view.selection.setVisible(this.selection.has(entityKey) && entity.alive);
    if (entityKey === this.focusedNinjaId)
      this.callbacks.onFocusedNinjaChanged(entity);
    if (!entity.alive) view.label.setVisible(false);
    view.hp.clear();
    const statuses = entity.statusEffects.split(",");
    view.sprite.setTint(
      statuses.includes("fear")
        ? 0xb5a1ff
        : statuses.includes("slow")
          ? 0x9bdcff
          : statuses.includes("counter_ready")
            ? 0xffd269
            : statuses.includes("reflex_guard")
              ? 0x9af0bd
              : 0xffffff,
    );
    view.hp.fillStyle(0x1b2118, 0.9).fillRoundedRect(0, 0, 24, 3, 1);
    const color =
      entity.entityType === "monster"
        ? 0xec6a50
        : entity.team === 2
          ? 0x8bb7ff
          : 0x75d18c;
    view.hp
      .fillStyle(color, 1)
      .fillRoundedRect(
        0,
        0,
        24 * Math.max(0, entity.hp / Math.max(1, entity.maxHp)),
        3,
        1,
      );
    if (entity.entityType === "ninja" && entity.maxChakra > 0) {
      view.hp.fillStyle(0x2d2a45, 1).fillRoundedRect(0, 5, 24, 2, 1);
      view.hp
        .fillStyle(0x78a6f0, 1)
        .fillRoundedRect(
          0,
          5,
          24 * Math.max(0, entity.chakra / entity.maxChakra),
          2,
          1,
        );
    }
  }

  private queueAppearanceAssets(appearance: SpriteAppearance): void {
    for (const direction of appearance.sourceDirections)
      for (const frame of appearance.sourceFrames) {
        const key = outfitTextureKey(appearance.id, direction, frame);
        this.load.image(key, sourceOutfitFrame(appearance, frame, direction));
      }
  }

  private ensureAppearanceLoaded(appearance: SpriteAppearance): boolean {
    const assets = appearance.sourceDirections.flatMap((direction) =>
      appearance.sourceFrames.map((frame) => ({
        key: outfitTextureKey(appearance.id, direction, frame),
        path: sourceOutfitFrame(appearance, frame, direction),
      })),
    );
    const missing = assets.filter((asset) => !this.textures.exists(asset.key));
    if (missing.length === 0) {
      this.ensureAppearanceAnimations(appearance);
      return true;
    }
    if (this.appearanceLoads.has(appearance.id)) return false;

    this.appearanceLoads.add(appearance.id);
    const pending = new Set(missing.map((asset) => asset.key));
    let failed = false;
    const finish = () => {
      if (pending.size > 0) return;
      this.load.off("loaderror", onLoadError);
      this.appearanceLoads.delete(appearance.id);
      if (failed) return;
      this.ensureAppearanceAnimations(appearance);
      this.room.state.entities.forEach((entity, key) => {
        if (entity.appearanceId === appearance.id)
          this.updateEntity(entity, String(key));
      });
    };
    const onLoadError = (file: { key: string }) => {
      if (!pending.delete(file.key)) return;
      failed = true;
      console.error(`Could not load outfit asset ${file.key}`);
      finish();
    };
    this.load.on("loaderror", onLoadError);
    for (const asset of missing) {
      this.load.once(`filecomplete-image-${asset.key}`, () => {
        pending.delete(asset.key);
        finish();
      });
      this.load.image(asset.key, asset.path);
    }
    if (!this.load.isLoading()) this.load.start();
    return false;
  }

  private ensureAppearanceAnimations(appearance: SpriteAppearance): void {
    this.animationController.registerAppearance(appearance);
  }

  private removeEntity(id: string): void {
    this.entityInstanceUnsubscribers.get(id)?.();
    this.entityInstanceUnsubscribers.delete(id);
    this.stopStatusEffectsForEntity(id);
    const view = this.rendered.get(id);
    if (!view) return;
    view.sprite.destroy();
    view.hp.destroy();
    view.label.destroy();
    view.selection.destroy();
    this.selection.delete(id);
    this.rendered.delete(id);
    if (this.focusedNinjaId === id) this.focusNinja([...this.selection].at(-1));
  }

  private playEvent(type: string, event: GameRoomEventPayload): void {
    if (type === "combat.guard_started") {
      if (event.x !== undefined && event.y !== undefined)
        this.playEffect(event.effectId ?? 161, event.x, event.y, {
          alpha: 0.78,
          scaleFrom: 0.55,
          scaleTo: 1.1,
          ...(event.durationMs === undefined
            ? {}
            : { durationMs: event.durationMs }),
        });
      if (event.entityId)
        this.showReactiveLabel(event.entityId, "GUARD", "#9af0bd");
      return;
    }
    if (type === "combat.guard_triggered") {
      if (event.x !== undefined && event.y !== undefined)
        this.playEffect(event.effectId ?? 161, event.x, event.y, {
          alpha: 0.9,
          scaleFrom: 0.55,
          scaleTo: 1.25,
        });
      if (event.entityId)
        this.showReactiveLabel(
          event.entityId,
          "PARRY -" + Math.round(event.mitigated ?? 0),
          "#8fe5df",
        );
      return;
    }
    if (type === "combat.counter_ready") {
      if (event.entityId)
        this.showReactiveLabel(event.entityId, "COUNTER READY", "#ffd269");
      return;
    }
    if (
      type === "combat.counter_consumed" ||
      type === "combat.reactive_expired"
    )
      return;
    if (type === "combat.shield_applied") {
      const target = event.targetId
        ? this.rendered.get(event.targetId)?.sprite
        : undefined;
      if (target && event.shieldAmount !== undefined) {
        const label = this.add
          .text(
            target.x,
            target.y - 48,
            `SHIELD +${Math.round(event.shieldAmount)}`,
            {
              color: "#9af0bd",
              fontFamily: "monospace",
              fontSize: "12px",
              stroke: "#182018",
              strokeThickness: 3,
            },
          )
          .setOrigin(0.5)
          .setDepth(1000);
        this.tweens.add({
          targets: label,
          y: label.y - 24,
          alpha: 0,
          duration: 900,
          onComplete: () => label.destroy(),
        });
      }
      return;
    }
    if (type === "combat.shield_removed") return;
    if (type === "combat.field_removed") {
      if (event.fieldId) {
        this.techniqueFieldVisuals.get(event.fieldId)?.destroy();
        this.techniqueFieldVisuals.delete(event.fieldId);
      }
      return;
    }
    if (type === "combat.field_pulse") {
      if (event.x !== undefined && event.y !== undefined)
        this.playEffect(event.effectId ?? 37, event.x, event.y, {
          alpha: 0.9,
          scaleFrom: 0.65,
          scaleTo: 1.5,
        });
      return;
    }
    if (type === "combat.field_placed" || type === "combat.status_removed")
      return;
    if (type === "combat.trap_placed") {
      this.startProximityTrapVisual(event);
      return;
    }
    if (type === "combat.trap_armed") {
      this.armProximityTrapVisual(event.trapId);
      return;
    }
    if (type === "combat.trap_removed") {
      this.removeProximityTrapVisual(event.trapId);
      return;
    }
    if (type === "combat.telegraph") {
      this.playTelegraph(event);
      return;
    }
    const source = event.sourceId
      ? this.rendered.get(event.sourceId)?.sprite
      : undefined;
    const target = event.targetId
      ? this.rendered.get(event.targetId)?.sprite
      : undefined;
    if (type === "combat.status_resisted") {
      const label = this.add
        .text(
          target?.x ?? event.x ?? 0,
          (target?.y ?? event.y ?? 0) - 28,
          "IMMUNE",
          {
            color: "#c8b6ff",
            fontFamily: "monospace",
            fontSize: "13px",
            stroke: "#182018",
            strokeThickness: 3,
          },
        )
        .setOrigin(0.5)
        .setDepth(1000);
      this.tweens.add({
        targets: label,
        y: label.y - 30,
        alpha: 0,
        duration: 780,
        onComplete: () => label.destroy(),
      });
      return;
    }
    if (type === "combat.projectile") {
      const fromX = event.fromX ?? source?.x ?? 0;
      const fromY = event.fromY ?? source?.y ?? 0;
      const toX = event.toX ?? target?.x ?? fromX;
      const toY = event.toY ?? target?.y ?? fromY;
      const resourceId = event.missileId ?? 15;
      const missileDefinition =
        visualMissileByResourceId(resourceId) ?? visualMissileByResourceId(15)!;
      const direction =
        event.projectileDirection ??
        projectileDirectionFromVector(toX - fromX, toY - fromY) ??
        "north";
      const variant = missileDefinition.directions[direction];
      const frames = variant.frames ?? [undefined];
      const textureKeys = frames.map((frame) =>
        missileTextureKey(missileDefinition.resourceId, direction, frame),
      );
      const flightDurationMs = event.flightDurationMs ?? 350;
      const missile = this.add
        .image(fromX, fromY, textureKeys[0]!)
        .setDepth(800);
      this.recordTechniqueVisual(
        "missile",
        resourceId,
        undefined,
        textureKeys[0],
      );
      let nextFrameIndex = 1;
      const frameTimer =
        textureKeys.length > 1
          ? this.time.addEvent({
              delay: Math.max(1, flightDurationMs / textureKeys.length),
              repeat: textureKeys.length - 2,
              callback: () => {
                if (missile.active)
                  missile.setTexture(
                    textureKeys[nextFrameIndex % textureKeys.length]!,
                  );
                nextFrameIndex += 1;
              },
            })
          : undefined;
      this.tweens.add({
        targets: missile,
        x: toX,
        y: toY,
        duration: flightDurationMs,
        onComplete: () => {
          frameTimer?.remove();
          missile.destroy();
        },
      });
    }
    const hasStatusLoop =
      type === "combat.status_applied" && event.statusEffectId !== undefined;
    if (hasStatusLoop) this.startStatusEffectLoop(event);
    if (
      [
        "combat.cast",
        "combat.projectile_armed",
        "combat.impact",
        "combat.hit",
        "combat.miss",
        "combat.heal",
        "combat.status_applied",
      ].includes(type) &&
      (type !== "combat.miss" ||
        (!event.techniqueId &&
          event.effectId === BASIC_ATTACK_EFFECT_IDS.miss)) &&
      !hasStatusLoop &&
      (target || source || event.x !== undefined)
    ) {
      const isWorldPointEvent =
        type === "combat.impact" || type === "combat.projectile_armed";
      const originX = isWorldPointEvent
        ? (event.x ?? 0)
        : type === "combat.cast"
          ? (event.x ?? source?.x ?? 0)
          : (target?.x ?? event.x ?? source?.x ?? 0);
      const originY = isWorldPointEvent
        ? (event.y ?? 0)
        : type === "combat.cast"
          ? (event.y ?? source?.y ?? 0)
          : (target?.y ?? event.y ?? source?.y ?? 0);
      const castVisual = type === "combat.cast" ? event.castVisual : undefined;
      const facing = effectFacing(event.direction);
      const isForwardCast = castVisual?.placement === "caster_front";
      const effectX =
        originX +
        (isForwardCast ? facing.x * (castVisual.forwardOffsetPx ?? 0) : 0);
      const effectY =
        originY +
        (isForwardCast ? facing.y * (castVisual.forwardOffsetPx ?? 0) : 0);
      const rotation = castVisual?.rotateToFacing ? facing.rotation : 0;
      if (
        event.effectId !== null &&
        (type !== "combat.hit" || event.effectId !== undefined)
      ) {
        this.playEffect(event.effectId ?? 37, effectX, effectY, {
          rotation,
          ...(castVisual?.scaleFrom === undefined
            ? {}
            : { scaleFrom: castVisual.scaleFrom }),
          ...(castVisual?.scaleTo === undefined
            ? {}
            : { scaleTo: castVisual.scaleTo }),
          ...(castVisual?.alpha === undefined
            ? {}
            : { alpha: castVisual.alpha }),
          ...(castVisual?.fadeOutMs === undefined
            ? {}
            : { fadeOutMs: castVisual.fadeOutMs }),
          ...(castVisual?.durationMs === undefined
            ? {}
            : { durationMs: castVisual.durationMs }),
          ...(event.durationMs === undefined
            ? {}
            : { durationMs: event.durationMs }),
        });
      }
      if (event.damage || event.healing) {
        const value = this.add
          .text(
            originX,
            originY - 28,
            event.healing
              ? `+${event.healing}`
              : `-${event.damage}${event.critical ? "!" : ""}`,
            {
              color: event.healing
                ? "#8ef0a8"
                : event.critical
                  ? "#ffd269"
                  : "#ff9080",
              fontFamily: "monospace",
              fontSize: "16px",
              stroke: "#182018",
              strokeThickness: 3,
            },
          )
          .setOrigin(0.5)
          .setDepth(1000);
        this.tweens.add({
          targets: value,
          y: originY - 65,
          alpha: 0,
          duration: 780,
          onComplete: () => value.destroy(),
        });
      }
      if (event.absorbed && event.absorbed > 0) {
        const absorbed = this.add
          .text(originX, originY - 46, `ABSORB ${Math.round(event.absorbed)}`, {
            color: "#8fe5df",
            fontFamily: "monospace",
            fontSize: "12px",
            stroke: "#182018",
            strokeThickness: 3,
          })
          .setOrigin(0.5)
          .setDepth(1000);
        this.tweens.add({
          targets: absorbed,
          y: absorbed.y - 28,
          alpha: 0,
          duration: 780,
          onComplete: () => absorbed.destroy(),
        });
      }
    }
  }

  /** Render the shield loop from authoritative room state, including on rejoin. */
  private syncShieldVisuals(time: number): void {
    const activeIds = new Set<string>();
    const now = Date.now();
    this.room.state.shields?.forEach((shield, shieldId) => {
      activeIds.add(shieldId);
      const target = this.rendered.get(shield.targetId)?.sprite;
      if (!target?.visible || !target.active || shield.expiresAt <= now) {
        this.removeShieldVisual(shieldId);
        return;
      }

      let visual = this.shieldVisuals.get(shieldId);
      if (!visual) {
        const definition = Object.values(VISUAL_EFFECTS).find(
          (effect) =>
            effect.directory === "effects" &&
            effect.resourceId === shield.visualEffectId,
        );
        const frames = [...(definition?.frames ?? [])];
        const textureKey = frames.length
          ? `effect-${shield.visualEffectId}-f${frames[0]}`
          : undefined;
        const image =
          textureKey && this.textures.exists(textureKey)
            ? this.add
                .image(target.x, target.y - 12, textureKey)
                .setScale(1.2)
                .setAlpha(0.92)
            : undefined;
        const halo = this.add.graphics();
        halo.fillStyle(0x55d98b, 0.08);
        halo.fillEllipse(0, 0, 24, 34);
        halo.lineStyle(2, 0x8ef0a8, 0.75);
        halo.strokeEllipse(0, 0, 24, 34);
        const created = {
          targetId: shield.targetId,
          resourceId: shield.visualEffectId,
          frames,
          image,
          halo,
          startedAt: time,
          frameIndex: 0,
        };
        this.shieldVisuals.set(shieldId, created);
        visual = created;
      }
      if (!visual) return;

      const remaining = Math.max(0, shield.expiresAt - now);
      const pulse = 0.78 + Math.sin(time / 135) * 0.12;
      visual.halo
        .setPosition(target.x, target.y - 15)
        .setDepth(target.depth + 2)
        .setAlpha(pulse * Math.min(1, remaining / 250));
      if (visual.image) {
        visual.image
          .setPosition(target.x, target.y - 15)
          .setDepth(target.depth + 3)
          .setAlpha(0.92 * Math.min(1, remaining / 250));
        if (visual.frames.length > 0) {
          const frameIndex =
            Math.floor(
              (time - visual.startedAt) / VISUAL_EFFECT_FRAME_DURATION_MS,
            ) % visual.frames.length;
          if (frameIndex !== visual.frameIndex) {
            visual.frameIndex = frameIndex;
            const nextTexture = `effect-${visual.resourceId}-f${visual.frames[frameIndex]}`;
            if (this.textures.exists(nextTexture))
              visual.image.setTexture(nextTexture);
          }
        }
      }
    });
    for (const shieldId of this.shieldVisuals.keys()) {
      if (!activeIds.has(shieldId)) this.removeShieldVisual(shieldId);
    }
  }

  private removeShieldVisual(shieldId: string): void {
    const visual = this.shieldVisuals.get(shieldId);
    if (!visual) return;
    visual.image?.destroy();
    visual.halo.destroy();
    this.shieldVisuals.delete(shieldId);
  }

  private playTelegraph(event: GameRoomEventPayload): void {
    const linePoints = event.linePoints?.filter(
      (point) => Number.isFinite(point.x) && Number.isFinite(point.y),
    );
    const hasLine = Boolean(linePoints && linePoints.length > 1);
    const hasCircle =
      event.x !== undefined &&
      event.y !== undefined &&
      event.radius !== undefined &&
      event.radius > 0;
    if (!hasLine && !hasCircle) return;
    const marker = this.add.graphics().setDepth(850);
    if (hasLine) {
      const drawPath = () => {
        marker.beginPath();
        linePoints!.forEach((point, index) => {
          if (index === 0) marker.moveTo(point.x, point.y);
          else marker.lineTo(point.x, point.y);
        });
        marker.strokePath();
      };
      marker.lineStyle(event.lineWidth ?? 48, 0x79b9ff, 0.18);
      drawPath();
      marker.lineStyle(2, 0xa7d7ff, 0.95);
      drawPath();
    } else {
      marker.setPosition(event.x!, event.y!);
      marker.fillStyle(0x79b9ff, 0.12);
      marker.fillCircle(0, 0, event.radius!);
      marker.lineStyle(2, 0xa7d7ff, 0.9);
      marker.strokeCircle(0, 0, event.radius!);
      marker.lineStyle(1, 0xd6ebff, 0.55);
      marker.strokeCircle(0, 0, Math.max(4, event.radius! - 5));
    }
    const duration = Math.max(1, event.durationMs ?? 1_200);
    this.tweens.add({
      targets: marker,
      alpha: 0.45,
      duration: 180,
      yoyo: true,
      repeat: Math.max(0, Math.floor(duration / 360) - 1),
    });
    this.time.delayedCall(duration, () => marker.destroy());
  }

  private showReactiveLabel(
    entityId: string,
    message: string,
    color: string,
  ): void {
    const sprite = this.rendered.get(entityId)?.sprite;
    if (!sprite?.active || !sprite.visible) return;
    const label = this.add
      .text(sprite.x, sprite.y - 40, message, {
        color,
        fontFamily: "monospace",
        fontSize: "12px",
        stroke: "#182018",
        strokeThickness: 3,
      })
      .setOrigin(0.5)
      .setDepth(1000);
    this.tweens.add({
      targets: label,
      y: label.y - 20,
      alpha: 0,
      duration: 850,
      onComplete: () => label.destroy(),
    });
  }

  private syncTechniqueFieldVisuals(now: number): void {
    const currentIds = new Set<string>();
    this.room.state.techniqueFields?.forEach((field, fieldId) => {
      currentIds.add(fieldId);
      let marker = this.techniqueFieldVisuals.get(fieldId);
      if (!marker) {
        marker = this.add
          .graphics()
          .setPosition(field.x, field.y)
          .setDepth(field.y - 2);
        marker.fillStyle(0x38a9ef, 0.12);
        marker.fillCircle(0, 0, field.radius);
        marker.lineStyle(2, 0x6fd6ff, 0.7);
        marker.strokeCircle(0, 0, field.radius);
        marker.lineStyle(1, 0xc6f4ff, 0.28);
        marker.strokeCircle(0, 0, Math.max(4, field.radius - 8));
        this.techniqueFieldVisuals.set(fieldId, marker);
        this.recordTechniqueVisual("field_marker", undefined, {
          roomId: this.room.state.instanceId,
          mode: this.mode,
          type: "combat.field_placed",
          techniqueId: field.techniqueId,
        });
      }
      marker
        .setPosition(field.x, field.y)
        .setDepth(field.y - 2)
        .setAlpha(0.82 + Math.sin(now / 190) * 0.12);
    });
    for (const [fieldId, marker] of this.techniqueFieldVisuals) {
      if (currentIds.has(fieldId)) continue;
      marker.destroy();
      this.techniqueFieldVisuals.delete(fieldId);
    }
  }

  private startProximityTrapVisual(event: GameRoomEventPayload): void {
    const trapId = event.trapId;
    if (!trapId || event.x === undefined || event.y === undefined) return;
    const resourceId = event.effectId ?? 458;
    const definition = Object.values(VISUAL_EFFECTS).find(
      (effect) =>
        effect.directory === "effects" && effect.resourceId === resourceId,
    );
    if (!definition?.frames.length) return;

    const now = this.time.now;
    const remainingLifetimeMs = Math.max(
      0,
      event.remainingLifetimeMs ?? 20_000,
    );
    const existing = this.proximityTrapVisuals.get(trapId);
    if (existing) {
      existing.expiresAt = now + remainingLifetimeMs;
      existing.armedAt = now + Math.max(0, event.remainingArmMs ?? 0);
      existing.armed = event.armed ?? existing.armed;
      existing.image
        .setPosition(event.x, event.y)
        .setAlpha(existing.armed ? 0.82 : 0.42);
      return;
    }

    const frames = [...definition.frames];
    const armed = event.armed ?? false;
    const image = this.add
      .image(event.x, event.y, `effect-${resourceId}-f${frames[0] ?? 0}`)
      .setOrigin(0.5)
      .setScale(0.7)
      .setAlpha(armed ? 0.82 : 0.42)
      .setDepth(Math.floor(event.y) - 1);
    this.proximityTrapVisuals.set(trapId, {
      resourceId,
      frames,
      image,
      startedAt: now,
      armedAt: now + Math.max(0, event.remainingArmMs ?? 0),
      expiresAt: now + remainingLifetimeMs,
      frameIndex: 0,
      armed,
    });
    this.recordTechniqueVisual("trap_marker", resourceId);
  }

  private armProximityTrapVisual(trapId?: string): void {
    if (!trapId) return;
    const trap = this.proximityTrapVisuals.get(trapId);
    if (!trap || trap.armed) return;
    trap.armed = true;
    trap.image.setAlpha(0.82);
    this.tweens.add({
      targets: trap.image,
      scale: 0.82,
      yoyo: true,
      duration: 220,
      repeat: 1,
    });
  }

  private updateProximityTrapVisuals(now: number): void {
    for (const [trapId, trap] of this.proximityTrapVisuals) {
      if (!trap.image.active || now >= trap.expiresAt) {
        this.removeProximityTrapVisual(trapId);
        continue;
      }
      if (!trap.armed && now >= trap.armedAt)
        this.armProximityTrapVisual(trapId);
      const frameIndex =
        Math.floor((now - trap.startedAt) / VISUAL_EFFECT_FRAME_DURATION_MS) %
        trap.frames.length;
      if (frameIndex !== trap.frameIndex) {
        trap.frameIndex = frameIndex;
        trap.image.setTexture(
          `effect-${trap.resourceId}-f${trap.frames[frameIndex] ?? 0}`,
        );
      }
      trap.image.setDepth(Math.floor(trap.image.y) - 1);
    }
  }

  private removeProximityTrapVisual(trapId?: string): void {
    if (!trapId) return;
    const trap = this.proximityTrapVisuals.get(trapId);
    if (!trap) return;
    trap.image.destroy();
    this.proximityTrapVisuals.delete(trapId);
  }

  private startStatusEffectLoop(event: GameRoomEventPayload): void {
    const targetId = event.targetId;
    const resourceId = event.statusEffectId;
    const durationMs = event.debuffDurationMs;
    if (!targetId || resourceId === undefined || !durationMs || durationMs <= 0)
      return;
    const target = this.rendered.get(targetId);
    const definition = Object.values(VISUAL_EFFECTS).find(
      (effect) =>
        effect.directory === "effects" && effect.resourceId === resourceId,
    );
    if (!target?.sprite.visible || !definition?.frames.length) return;
    const loopKey = `${targetId}:${event.status ?? resourceId}`;
    this.stopStatusEffectLoop(loopKey);
    const frames = [...definition.frames];
    const image = this.add
      .image(
        target.sprite.x,
        target.sprite.y,
        `effect-${resourceId}-f${frames[0] ?? 0}`,
      )
      .setDepth(target.sprite.depth + 1)
      .setAlpha(0.95);
    this.statusEffectLoops.set(loopKey, {
      targetId,
      resourceId,
      frames,
      image,
      startedAt: performance.now(),
      durationMs,
      frameIndex: 0,
    });
    this.recordTechniqueVisual("status_loop", resourceId);
  }

  private updateStatusEffectLoops(now: number): void {
    for (const [loopKey, loop] of this.statusEffectLoops) {
      const target = this.rendered.get(loop.targetId)?.sprite;
      const elapsed = now - loop.startedAt;
      if (!target?.visible || elapsed >= loop.durationMs) {
        this.stopStatusEffectLoop(loopKey);
        continue;
      }
      const frameIndex =
        Math.floor(elapsed / VISUAL_EFFECT_FRAME_DURATION_MS) %
        Math.max(1, loop.frames.length);
      loop.image.setPosition(target.x, target.y).setDepth(target.depth + 1);
      if (frameIndex !== loop.frameIndex) {
        loop.frameIndex = frameIndex;
        loop.image.setTexture(
          `effect-${loop.resourceId}-f${loop.frames[frameIndex] ?? 0}`,
        );
      }
    }
  }

  private stopStatusEffectLoop(loopKey: string): void {
    const loop = this.statusEffectLoops.get(loopKey);
    if (!loop) return;
    loop.image.destroy();
    this.statusEffectLoops.delete(loopKey);
  }

  private stopStatusEffectsForEntity(targetId: string): void {
    for (const [loopKey, loop] of this.statusEffectLoops) {
      if (loop.targetId === targetId) this.stopStatusEffectLoop(loopKey);
    }
  }

  private playEffect(
    resourceId: number,
    x: number,
    y: number,
    options: {
      rotation?: number;
      scaleFrom?: number;
      scaleTo?: number;
      alpha?: number;
      fadeOutMs?: number;
      durationMs?: number;
    } = {},
  ): void {
    const definition = Object.values(VISUAL_EFFECTS).find(
      (effect) =>
        effect.directory === "effects" && effect.resourceId === resourceId,
    );
    const frames = definition?.frames ?? VISUAL_EFFECTS.impact.frames;
    const initialScale = options.scaleFrom ?? 0.5;
    const duration = options.durationMs ?? Math.max(360, frames.length * 55);
    const fadeDuration = Math.min(options.fadeOutMs ?? duration, duration);
    const textureKey = `effect-${definition?.resourceId ?? 37}-f${frames[0] ?? 0}`;
    const image = this.add
      .image(x, y, textureKey)
      .setDepth(900)
      .setRotation(options.rotation ?? 0)
      .setScale(initialScale)
      .setAlpha(options.alpha ?? 0.95);
    this.recordTechniqueVisual(
      "effect",
      definition?.resourceId ?? 37,
      undefined,
      textureKey,
    );
    this.tweens.add({
      targets: image,
      scale: options.scaleTo ?? 1.35,
      duration,
      onComplete: () => image.destroy(),
    });
    this.tweens.add({
      targets: image,
      alpha: 0,
      duration: fadeDuration,
      delay: duration - fadeDuration,
    });
    frames.slice(1).forEach((frame, index) => {
      this.time.delayedCall(
        (index + 1) * VISUAL_EFFECT_FRAME_DURATION_MS,
        () => {
          if (image.active)
            image.setTexture(
              `effect-${definition?.resourceId ?? 37}-f${frame}`,
            );
        },
      );
    });
  }
}
